import { GalateaOfferExperimentFlag } from './GalateaFlagsDefinition';

export const OFFER_BUCKET_MAX = 99;

export type OfferBucketRange = readonly [from: number, to: number];

/**
 * `paywallDomain` splits every user once, before any layer. The earmarks are reserved but not carved out,
 * so `overlapping` spans every bucket.
 */
export const paywallDomainAllocation = {
  holdoutEarmark: [0, 4],
  wholePaywallCarveoutEarmark: [5, 19],
  overlapping: [0, 99],
} as const satisfies Record<string, OfferBucketRange>;

export type OfferExperimentClaim = {
  flag: GalateaOfferExperimentFlag;
  range: OfferBucketRange;
  weights: Record<string, number>;
  structureChanging?: true;
};

export type OfferLayerAudienceAllocation = {
  presentationSafeBelow: number;
  claims: readonly OfferExperimentClaim[];
};

/**
 * One audience per Unleash market segment; `Market: other` also catches a missing `storeCountry`. Strategy
 * order only resolves within one flag, so a catch-all strategy must exclude every audience another flag
 * claims in full.
 */
export const OFFER_LAYER_AUDIENCES = ['US', 'DE', 'INTL_TIER_0', 'OTHER'] as const;

export type OfferLayerAudience = (typeof OFFER_LAYER_AUDIENCES)[number];

const halfAndHalf = (treatment: string): Record<string, number> => ({ control: 500, [treatment]: 500 });

/**
 * Mirror of the `offerBucket` claims in the Unleash strategies. Ranges are inclusive and disjoint within an
 * audience, and unclaimed buckets get the baseline. Weights are Unleash variant weights out of 1000.
 * `structureChanging` claims sit at the top of the range so a presentation test can exclude them all with
 * `offerBucket NUM_LT <presentationSafeBelow>`.
 */
export const offerLayerAllocation: Record<OfferLayerAudience, OfferLayerAudienceAllocation> = {
  US: {
    presentationSafeBelow: 100,
    claims: [
      { flag: 'testOfferGoldenHourV2', range: [0, 9], weights: halfAndHalf('golden_hour_v2') },
      {
        flag: 'testOfferSplitTextAndAudiobookV3',
        range: [10, 19],
        weights: halfAndHalf('split_text_and_audiobook__v3'),
      },
      { flag: 'testOfferYearly7499WithFamilyV2', range: [20, 29], weights: halfAndHalf('yearly_7499_with_family_v2') },
    ],
  },
  DE: {
    presentationSafeBelow: 100,
    claims: [
      { flag: 'testOfferGoldenHourV2', range: [0, 9], weights: halfAndHalf('golden_hour_v2') },
      {
        flag: 'testOfferSplitTextAndAudiobookV3',
        range: [10, 19],
        weights: halfAndHalf('split_text_and_audiobook__v3'),
      },
      {
        flag: 'testOfferPostPurchaseRenewalOffer',
        range: [20, 29],
        weights: halfAndHalf('post_purchase_renewal_offer'),
      },
    ],
  },
  INTL_TIER_0: {
    presentationSafeBelow: 100,
    claims: [{ flag: 'testOfferIntlTierPricing', range: [0, 99], weights: halfAndHalf('intl_tier_0') }],
  },
  OTHER: {
    presentationSafeBelow: 100,
    claims: [{ flag: 'testOfferGoldenHourV2', range: [0, 9], weights: halfAndHalf('golden_hour_v2') }],
  },
};

/** Declared flags and arms with no claim, kept so a relaunch only needs a claim here and Unleash strategies. */
export const idleOfferExperiments = [
  'testOfferPointsLimitedSubscription',
] as const satisfies readonly GalateaOfferExperimentFlag[];

export const idleOfferArms: Partial<Record<GalateaOfferExperimentFlag, readonly string[]>> = {
  testOfferIntlTierPricing: ['intl_tier_2', 'aa_variant'],
};

export const offerClaimShare = ({ range: [from, to] }: OfferExperimentClaim): number => to - from + 1;

export const offerArmShare = (claim: OfferExperimentClaim, arm: string): number =>
  (offerClaimShare(claim) * (claim.weights[arm] ?? 0)) / 1000;

export const offerBaselineShare = (audience: OfferLayerAudience): number =>
  OFFER_BUCKET_MAX + 1 - offerLayerAllocation[audience].claims.reduce((sum, claim) => sum + offerClaimShare(claim), 0);
